import sortBy from 'lodash.sortby'
import anime from 'animejs/lib/anime.es'
import { mapActions, mapGetters } from 'vuex'
import seededRandom from 'seed-random'
import { getRandomIntInRange } from '@/utils'
import { registerSound } from '@/modules/sound'
import {
  GAME_STATUS, SIDE_HEADS, SIDE_TAILS,
  MIN_FLIPS, MAX_FLIPS,
} from '@/views/games/Coinflip/utils'
import CoinflipGame from '@/store/models/games/coinflip/CoinflipGame'
import CoinflipPlayer from './components/CoinflipPlayer'
import CoinflipItem from './components/CoinflipItem'
import CoinflipCoin from './components/CoinflipCoin'

const sounds = {
  toss: registerSound('games/coinflip/toss', { volume: 0.5 }),
  sheen: registerSound('games/coinflip/sheen', { volume: 0.4 }),
  glow: registerSound('games/coinflip/glow', { volume: 0.4 }),
  impact: [
    registerSound('games/coinflip/impact1', { volume: 0.15 }),
    registerSound('games/coinflip/impact2', { volume: 0.15 }),
    registerSound('games/coinflip/impact3', { volume: 0.15 }),
    registerSound('games/coinflip/impact4', { volume: 0.15 }),
  ],
  win: registerSound('games/wheel/win', { volume: 0.3 }),
}

const defaultSoundTriggers = () => [
  'toss',
  'sheen',
  'impact1',
  'impact2',
  'impact3',
  'glow',
]

let lastProgress = null

export default {
  components: {
    CoinflipPlayer,
    CoinflipItem,
    CoinflipCoin,
  },
  props: {
    close: {
      type: Function,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: {
        join: false,
        bandit: false,
      },
      showTaxInfo: false,
      animation: null,
      countdownValue: 0,
      soundTriggers: defaultSoundTriggers(),
      rolling: false,
      canCallBandit: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
      conga: 'conga/conga',
      itemdb: 'items/items',
      settings: 'settings/settings',
    }),
    muted() {
      return !this.settings?.games?.coinflip?.sounds
    },
    game() {
      const { game } = this.payload

      // if hasnt rolled yet, or invalid game obj, find in state
      if (!game.rolledAt) {
        return CoinflipGame.find(game.id)
      }

      return game
    },
    gameConfig() {
      return this.conga.games.coinflip
    },
    items() {
      const { itemdb } = this

      const items = sortBy(this.game.items.map(item => ({
        ...item,
        ...itemdb[item.name],
      })), 'price').reverse()

      return {
        pot: items,
        heads: items.filter(i => i.side === SIDE_HEADS),
        tails: items.filter(i => i.side === SIDE_TAILS),
      }
    },
    GAME_STATUS() {
      return GAME_STATUS
    },
    joinable() {
      if (!this.game.joinable) return false
      if (this.isCreator) return false

      return true
    },
    isCreator() {
      if (!this.user) return false

      return this.user?.steamid === this.game.creatorId
    },
    shouldShowStatus() {
      if (this.rolling) return false

      return this.game.status === GAME_STATUS.locked
    },
    _canCallBandit() {
      const { gameConfig: config, game } = this

      if (!this.isCreator) return false
      if (game.status !== GAME_STATUS.open) return false
      if (config.botJoinDisabled) return false

      const { callBanditAvailableAt } = this

      return callBanditAvailableAt < Date.now()
    },
    callBanditAvailableAt() {
      const { gameConfig: config, game } = this

      const createdAt = new Date(game.createdAt).getTime()
      const canCallAfter = config.time.botJoinAvailableAfter

      return createdAt + canCallAfter
    },
    locksIn() {
      return Math.max(0, Math.ceil((this.game.lockingAt - Date.now()) / 1e3))
    },
    progressValue() {
      return (((this.countdownValue * 1000) - 1) / this.gameConfig.time.countdown) * 100
    },
  },
  created() {
    if (!this.game) {
      this.close()
    }

    this.countdownValue = this.gameConfig.time.countdown / 1e3

    this.canCallBandit = this._canCallBandit
    // check if we're subbed
  },
  beforeDestroy() {
    // kill anim
    if (this.animation) {
      this.animation.pause()
      this.animation.animatables.forEach(({ target }) => {
        anime.remove(target)
      })
    }
  },
  watch: {
    'game.status': {
      async handler(status, oldStatus) {
        if (this.rolling) return
        if (oldStatus && oldStatus > GAME_STATUS.locked) return

        // we rolling for the first time
        if (status > GAME_STATUS.locked) {
          await this.$nextTick()
          this.roll(!!oldStatus)

          if (oldStatus) {
            this.showTaxInfo = true
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      join: 'games/coinflip/join',
    }),
    async onJoin() {
      if (this.loading.join) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      this.loading.join = true

      try {
        const { game } = this

        const modal = this.$modal.open(
          'backpack',
          { hasReturnValue: true, maxWidth: 900, noShadow: true },
          {
            maxItemSelect: this.gameConfig.maxItems,
            minValue: game.range[0],
            maxValue: game.range[1],
            hideUnderMax: true,
            dollarValue: true,
            autoselect: true,
          },
        )

        const modalItems = await modal.promise
        if (!modalItems) return

        const ids = modalItems.map(i => i.id)
        await this.join({ id: game.id, ids })
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.join = false
      }
    },
    onTick(val) {
      this.countdownValue = val
    },
    roll(firstTime) {
      if (this.rolling) return
      this.rolling = true

      const { game } = this

      const winningSide = game.winner.side

      console.log(`Coinflip ${game.id} rolled ${game.outcome} (Side: ${winningSide})`)

      // setup sound triggers
      this.soundTriggers = defaultSoundTriggers()
      lastProgress = 0

      const $coin = this.$refs.coin.$el
      const $coinCtn = $coin.parentElement

      const rand = seededRandom(game.id)()

      const flipCount = Math.floor(rand * (MIN_FLIPS - MAX_FLIPS) + MAX_FLIPS)
      const flips = 180 * flipCount
      const winSide = flipCount % 2 !== winningSide ? 180 : 360

      const rotation = flips + winSide

      const randXFlip = rand * (-90 - 90) + 90

      this.animation = anime.timeline({
        autoplay: false,
        elasticity: 0,
        easing: 'linear',
        update: this.onUpdate,
      })
        // coin zoom
        .add({
          targets: $coinCtn,
          scale: [
            {
              value: 1.8,
              delay: 500,
              duration: 1000,
              easing: 'easeOutQuad',
            },
            {
              value: 1,
              delay: 200,
              duration: 1000,
              easing: 'easeOutBounce',
            },
          ],
        }, 0)
        // coin flip
        .add({
          targets: $coin,
          rotateZ: [
            {
              value: randXFlip,
              duration: 200,
              easing: 'easeOutQuart',
            },
          ],
          rotateX: [
            {
              value: flips - 45,
              duration: 700,
              easing: 'easeOutCirc',
            },
            {
              value: flips + 45,
              duration: 600,
              easing: 'linear',
            },
            {
              value: rotation,
              duration: 800,
              easing: 'easeOutExpo',
            },
          ],
          begin(anim) {
            setTimeout(() => {
              $coin.classList.add('sheen')

              setTimeout(() => {
                $coin.classList.remove('sheen')
              }, 1500)
            }, 650)
          },
        }, 500)
        // shadow
        .add({
          targets: '.coin-shadow',
          opacity: [
            { value: 0.7, duration: 100 },
            { value: 0, delay: 1900, duration: 200 },
          ],
          scale: [
            { value: 1, duration: 100 },
            { value: 0.5, duration: 800 },
            {
              value: 1, delay: 500, duration: 800, easing: 'easeOutBounce',
            },
          ],
          backgroundColor: [
            { value: 'rgb(0,0,0)', duration: 1 },
          ],
          duration: 2000,
        }, 500)
        // smoke explosion
        .add({
          targets: '.sprite-smoke',
          opacity: [
            { value: 0, duration: 1 },
            { value: 0.4, duration: 1 },
            { value: 0, delay: 400, duration: 1 },
          ],
          backgroundPosition: [0, '-2400px'],
          duration: 500,
          easing: 'steps(12)',
        }, 2000)
        // board shake
        .add({
          targets: '.coin-board',
          translateX: [
            { value: 6, duration: 50 },
            { value: -4, duration: 50 },
            { value: 2, duration: 50 },
            { value: 0, duration: 50 },
          ],
          translateY: [
            { value: 8, duration: 70 },
            { value: -5, duration: 70 },
            { value: 3, duration: 70 },
            { value: 0, duration: 70 },
          ],
        }, 2000)
        // coin readjust
        .add({
          targets: $coinCtn,
          scale: [
            {
              value: 1.3,
              duration: 1200,
              easing: 'easeOutElastic',
            },
            {
              value: 1,
              delay: 800,
              duration: 1000,
              easing: 'easeOutExpo',
            },
          ],
        }, 3300)
        .add({
          targets: $coin,
          rotateZ: 0,
          rotateX: [
            { value: rotation - 25, duration: 1000 },
            { value: rotation, delay: 1100, duration: 1000 },
          ],
          easing: 'easeOutElastic',
          begin(anim) {
            setTimeout(() => {
              $coin.classList.add('sheen')

              setTimeout(() => {
                $coin.classList.remove('sheen')
              }, 1500)
            }, 350)
          },
        }, 3300)
        // gold halo
        .add({
          targets: '.coin-shadow',
          opacity: [
            { value: 0.5, duration: 300 },
            { value: 0, delay: 2100, duration: 200 },
          ],
          scale: [
            { value: 1, duration: 100 },
            { value: 1.15, duration: 1000 },
            {
              value: 1, delay: 500, duration: 1000,
            },
          ],
          backgroundColor: [{ value: 'hsl(49, 100%, 50%)', duration: 1 }],
        }, 3600)

      this.animation.play()

      setTimeout(() => {
        this.rolling = false
        this.onRollFinished(firstTime)
      }, this.animation.duration)
    },
    async onCallBandit() {
      if (this.loading.bandit) return

      this.loading.bandit = true

      try {
        const { game } = this

        await this.$socket.request('game.coinflip.requestBot', game.id)
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.bandit = false
      }
    },
    onBanditCountdownFinished() {
      this.canCallBandit = true
    },
    onUpdate(anim) {
      if (this.muted) return

      const progress = Math.floor(anim.progress)

      if (progress > lastProgress) {
        lastProgress = progress
        this.onProgress(progress)
      }
    },
    onProgress(progress) {
      // one-time sounds
      const { soundTriggers } = this
      if (!soundTriggers.length) return

      if (progress === 6 && soundTriggers.includes('toss')) {
        sounds.toss.play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'toss')
      }

      if (progress === 14 && soundTriggers.includes('sheen')) {
        sounds.sheen.play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'sheen')
      }

      if (progress === 33 && soundTriggers.includes('impact1')) {
        sounds.impact[getRandomIntInRange(0, 3)].play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'impact1')
      }

      if (progress === 35 && soundTriggers.includes('impact2')) {
        sounds.impact[getRandomIntInRange(0, 3)].play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'impact2')
      }

      if (progress === 40 && soundTriggers.includes('impact3')) {
        sounds.impact[getRandomIntInRange(0, 3)].play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'impact3')
      }

      if (progress === 58 && soundTriggers.includes('glow')) {
        sounds.glow.play()
        this.soundTriggers = soundTriggers.filter(i => i !== 'glow')
      }
    },
    onRollFinished(firstTime) {
      if (this.user?.steamid === this.game.winner?.steamid) {
        if (firstTime) {
          this.$toast.success('Your items are in your backpack.', {
            title: 'Coinflip won',
            sound: sounds.win,
          })

          this.close()
          this.$modal.open('coinflip-won', { maxWidth: 400 }, { game: this.game })
        }
      }
    },
  },

}
